// Routes.js - Routes Main File

import React, { useMemo } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

// COMPONENTS
import Dashboard from '../pages/Home/Dashboard';

import AddressBook from '../pages/Websms/AddressBook';
import BulkSend from '../pages/Websms/BulkSend';
import Compose from '../pages/Websms/Compose';
import Errors from '../pages/Websms/Errors';
import Inbox from '../pages/Websms/Inbox';
import Sent from '../pages/Websms/Sent';
import Scheduled from '../pages/Websms/Scheduled';
import Settings from '../pages/Websms/Settings';
import Template from '../pages/Websms/Templates';

import AdminDebug from '../pages/Admin/Debug';
import AdminFindCustomer from '../pages/Admin/FindCustomer';
import BrandViewPage from '../pages/Admin/BrandView';
import CampaignViewPage from '../pages/Admin/CampaignView';
import CompliancePage from '../pages/Admin/Compliance';
import Customers from '../pages/Admin/Customers';
import CustomerView from '../pages/Admin/CustomerView';
import CustomerProfile from '../pages/Admin/CustomerProfile';
import ErrorLog from '../pages/Admin/ErrorLog';
import Providers from '../pages/Admin/Providers';
import ServiceConfig from '../pages/Admin/ServiceConfig';
import SystemMonitor from '../pages/Admin/SystemMonitor';
import Tariffing from '../pages/Admin/Tariffing';
import TraceLog from '../pages/Admin/TraceLog';

import BillingSummary from '../pages/Billing/BillingsSummary';
import CommissionReport from '../pages/Billing/CommissionReport';
import ExpiredCards from '../pages/Billing/ExpiredCardsReport';
import Invoices from '../pages/Billing/Invoices';
import InvoiceShow from '../pages/Billing/InvoiceShow';
import NewCustomerReport from '../pages/Billing/NewCustomerReport';
import PaymentMethod from '../pages/Billing/PaymentMethod';
import PurchaseMessages from '../pages/Billing/Purchase';
import ProviderReports from '../pages/Reports/ProviderReports';

import AllMessagesReports from '../pages/Reports/AllMessages';
import AllReplyReports from '../pages/Reports/AllReply';
import CarrierReports from '../pages/Reports/CarrierReports';
import CostCenterReports from '../pages/Reports/CostCentreReports';
import CustomerReports from '../pages/Reports/CustomerReports';
import IncomingReports from '../pages/Reports/IncomingReports';
import MessageReports from '../pages/Reports/MessageReports';
import OutgoingReports from '../pages/Reports/OutgoingReports';
import ReplyReports from '../pages/Reports/ReplyReports';
import TopCustomerReports from '../pages/Reports/TopCustomerReports';
import UserReports from '../pages/Reports/UserReports';

import Administrators from '../pages/Account/Administrators';
import CostCenters from '../pages/Account/CostCenters';
import EditAdministrator from '../pages/Account/EditAdministrator';
import EditUser from '../pages/Account/EditUser';
import NewAdministrator from '../pages/Account/NewAdministrator';
import NewUser from '../pages/Account/NewUser';
import UserList from '../pages/Account/Users';

import Login from '../pages/Auth/Login';
import ForgotPassword from '../pages/Auth/PasswordRecovery';
import NewPassword from '../pages/Auth/NewPassword';
import Register from '../pages/Auth/Register';
import CompanyProfile from '../pages/Account/CompanyProfile';

const AppRoutes = ({ nonWebSmsProps, webSmsProps }) => {
  const isAuthenticated = useMemo(
    () => !!localStorage.getItem('rox_user_token'),
    []
  );

  return (
    <Routes>
      {/* Public Routes */}
      {!isAuthenticated ? (
        <>
          <Route path="/" element={<Login {...nonWebSmsProps} />} />
          <Route path="/login" element={<Login {...nonWebSmsProps} />} />
          <Route path="/register" element={<Register {...nonWebSmsProps} />} />
          <Route
            path="/forgotpassword"
            element={<ForgotPassword {...nonWebSmsProps} />}
          />
          <Route
            path="/recoverpassword/verify/:uniqueUrl"
            element={<NewPassword {...nonWebSmsProps} />}
          />
          <Route path="/*" element={<Navigate to="/" replace />} />
        </>
      ) : (
        <>
          {/* Users */}
          <Route path="account">
            <Route
              path="administrators"
              element={<Administrators {...nonWebSmsProps} />}
            />
            <Route
              path="administrators/new"
              element={<NewAdministrator {...nonWebSmsProps} />}
            />
            <Route
              path="administrators/:id"
              element={<EditAdministrator {...nonWebSmsProps} />}
            />
            <Route
              path="cost-centers"
              element={<CostCenters {...nonWebSmsProps} />}
            />
            <Route path="users" element={<UserList {...nonWebSmsProps} />} />
            <Route
              path="users/:id"
              element={<EditUser {...nonWebSmsProps} />}
            />
            <Route path="users/new" element={<NewUser {...nonWebSmsProps} />} />

            <Route
              path="company-profile"
              element={<CompanyProfile {...nonWebSmsProps} />}
            />
          </Route>

          {/* Billing */}
          <Route path="billing">
            <Route path="invoices" element={<Invoices {...nonWebSmsProps} />} />
            <Route
              path="payment-method"
              element={<PaymentMethod {...nonWebSmsProps} />}
            />
            <Route
              path="invoiceshow/:customer/:tab"
              element={<InvoiceShow {...nonWebSmsProps} />}
            />
            <Route
              path="summary"
              element={<BillingSummary {...nonWebSmsProps} />}
            />
            <Route
              path="new-customer-report"
              element={<NewCustomerReport {...nonWebSmsProps} />}
            />
            <Route
              path="commission-report"
              element={<CommissionReport {...nonWebSmsProps} />}
            />
            <Route
              path="expired-cards"
              element={<ExpiredCards {...nonWebSmsProps} />}
            />
            <Route
              path="purchase"
              element={<PurchaseMessages {...nonWebSmsProps} />}
            />
          </Route>

          {/* Reports */}
          <Route path="reports">
            <Route
              path="allsends"
              element={<AllMessagesReports {...nonWebSmsProps} />}
            />
            <Route
              path="customer"
              element={<CustomerReports {...nonWebSmsProps} />}
            />
            <Route
              path="cost-center"
              element={<CostCenterReports {...nonWebSmsProps} />}
            />
            <Route path="user" element={<UserReports {...nonWebSmsProps} />} />
            <Route
              path="message"
              element={<MessageReports {...nonWebSmsProps} />}
            />
            <Route
              path="reply"
              element={<ReplyReports {...nonWebSmsProps} />}
            />
            <Route
              path="allreply"
              element={<AllReplyReports {...nonWebSmsProps} />}
            />
            <Route
              path="incoming"
              element={<IncomingReports {...nonWebSmsProps} />}
            />
            <Route
              path="outgoing"
              element={<OutgoingReports {...nonWebSmsProps} />}
            />
            <Route
              path="provider"
              element={<ProviderReports {...nonWebSmsProps} />}
            />
            <Route
              path="top-user"
              element={<TopCustomerReports {...nonWebSmsProps} />}
            />
            <Route
              path="carrier"
              element={<CarrierReports {...nonWebSmsProps} />}
            />
          </Route>

          {/* WebSMS */}
          <Route path="websms">
            <Route path="compose" element={<Compose {...webSmsProps} />} />
            <Route path="bulk-send" element={<BulkSend {...webSmsProps} />} />
            <Route path="inbox" element={<Inbox {...webSmsProps} />} />
            <Route path="sent" element={<Sent {...webSmsProps} />} />
            <Route path="scheduled" element={<Scheduled {...webSmsProps} />} />
            <Route path="errors" element={<Errors {...webSmsProps} />} />
            <Route path="templates" element={<Template {...webSmsProps} />} />
            <Route
              path="address_book"
              element={<AddressBook {...webSmsProps} />}
            />
            <Route path="settings" element={<Settings {...webSmsProps} />} />
          </Route>

          {/* Admin */}
          <Route path="admin">
            <Route
              path="provider"
              element={<Providers {...nonWebSmsProps} />}
            />
            <Route
              path="customers"
              element={<Customers {...nonWebSmsProps} />}
            />
            <Route
              path="customers/:id"
              element={<CustomerView {...nonWebSmsProps} />}
            />
            <Route
              path="customers/:id/profile"
              element={<CustomerProfile {...nonWebSmsProps} />}
            />
            <Route path="errorlog" element={<ErrorLog {...nonWebSmsProps} />} />
            <Route path="tracelog" element={<TraceLog {...nonWebSmsProps} />} />

            <Route
              path="service-config"
              element={<ServiceConfig {...nonWebSmsProps} />}
            />
            <Route
              path="service-config/:tab"
              element={<ServiceConfig {...nonWebSmsProps} />}
            />
            <Route
              path="service-config/:tab/:id"
              element={<ServiceConfig {...nonWebSmsProps} />}
            />
            <Route
              path="service-config/:tab/:id/:child/:childId"
              element={<ServiceConfig {...nonWebSmsProps} />}
            />
            <Route
              path="service-config/:tab/:id/:child/:childId/:grandChild/:grandChildId"
              element={<ServiceConfig {...nonWebSmsProps} />}
            />

            <Route path="tariffs" element={<Tariffing {...nonWebSmsProps} />} />
            <Route
              path="find-customer"
              element={<AdminFindCustomer {...nonWebSmsProps} />}
            />
            <Route
              path="system-monitor"
              element={<SystemMonitor {...nonWebSmsProps} />}
            />
            <Route path="debug" element={<AdminDebug {...nonWebSmsProps} />} />
            <Route
              path="compliance"
              element={<CompliancePage {...nonWebSmsProps} />}
            />
            <Route
              path="compliance/brand/:id"
              element={<BrandViewPage {...nonWebSmsProps} />}
            />
            <Route
              path="compliance/campaign"
              element={<CampaignViewPage {...nonWebSmsProps} />}
            />
            <Route
              path="compliance/campaign/:id"
              element={<CampaignViewPage {...nonWebSmsProps} />}
            />
          </Route>

          {/* Home & Fallback */}
          <Route path="/home" element={<Dashboard {...webSmsProps} />} />
          <Route path="/*" element={<Navigate to="/home" replace />} />
        </>
      )}
    </Routes>
  );
};

export default AppRoutes;
